import type { ComponentProps } from "react";
import type { Rendition } from "@/lib/optimized-images";

type ResponsiveImageProps = {
  rendition: Rendition;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  decoding?: "async" | "auto" | "sync";
  sizes?: string;
  // fetchPriority is a valid HTML attribute not yet in TS JSX types
  fetchpriority?: "high" | "low" | "auto";
} & Omit<ComponentProps<"img">, "src" | "srcSet">;

/**
 * Renders a <picture> with AVIF (preferred) and WebP (fallback) sources.
 *
 * Uses **width descriptors** (e.g. `634w, 1268w`) instead of density
 * descriptors (`1x, 2x`) so the browser honors the `sizes` attribute and
 * downloads the smallest sufficient file for the actual rendered box. With
 * density descriptors the `sizes` hint is ignored and 2x-DPR devices always
 * fetch the largest rendition — the source of the "image larger than it needs
 * to be" warning. The 1x rendition is `rendition.w` wide; the 2x is `2x`.
 */
export default function ResponsiveImage({
  rendition,
  alt,
  className,
  loading = "lazy",
  decoding = "async",
  sizes,
  fetchpriority,
  ...rest
}: ResponsiveImageProps) {
  const w1 = rendition.w;
  const w2 = rendition.w * 2;

  // No optimized renditions exist for this asset yet — render a single
  // <img> pointing at the original so it actually displays.
  if (rendition.plainSrc) {
    return (
      <img
        src={rendition.plainSrc}
        alt={alt}
        width={rest.width ?? rendition.w}
        height={rest.height ?? rendition.h}
        loading={loading}
        decoding={decoding}
        className={className}
        {...(fetchpriority ? { fetchpriority } : {})}
        {...rest}
      />
    );
  }

  return (
    <picture>
      <source
        type="image/avif"
        srcSet={`${rendition.avif1} ${w1}w, ${rendition.avif2} ${w2}w`}
        sizes={sizes}
      />
      <source
        type="image/webp"
        srcSet={`${rendition.webp1} ${w1}w, ${rendition.webp2} ${w2}w`}
        sizes={sizes}
      />
      <img
        src={rendition.fallback}
        alt={alt}
        width={rest.width ?? rendition.w}
        height={rest.height ?? rendition.h}
        sizes={sizes}
        loading={loading}
        decoding={decoding}
        className={className}
        {...(fetchpriority
          ? {
              fetchpriority,
            }
          : {})}
        {...rest}
      />
    </picture>
  );
}
